import React, { FC, useMemo } from 'react';
import { Asset, Button, CheckmarkIcon } from '@clearcover/atlas';
import cx from 'classnames';

import { PaymentConceptAmount } from './PaymentConceptAmount';
import { PaymentConceptText } from './PaymentConceptText';
import { PaymentPlanOptionProps, PaymentConceptName } from './types';
import styles from './styles.module.css';

export const PaymentPlanOption: FC<PaymentPlanOptionProps> = ({
  plan,
  selected,
  loading,
  breakdownSequence,
  selectedLabel = 'Selected',
  variant,
  conceptLabels,
  onSelect,
}) => {
  const isGridVariant = variant === 'grid';

  // Create a map for quick lookup by name
  const breakdownMap = useMemo(() => {
    const map = new Map<PaymentConceptName, typeof plan.breakdown[number]>();

    for (const item of plan.breakdown) {
      map.set(item.name, item);
    }

    return map;
  }, [plan]);

  const breakdown = useMemo(() => {
    if (!breakdownSequence) return plan.breakdown;

    return breakdownSequence.map(key =>
      key === null ? null : breakdownMap.get(key) ?? null
    );
  }, [breakdownSequence, breakdownMap, plan.breakdown]);

  const getConceptLabel = (concept: NonNullable<typeof breakdown[number]>) =>
    conceptLabels?.[concept.name] ?? concept.label;

  const renderBreakdownItem = (
    concept: typeof breakdown[number],
    idx: number
  ) => {
    if (!concept) {
      // Empty visual slot for empty entries
      return (
        <div
          key={`empty-${idx}`}
          className={cx(styles.emptySlot, styles.paymentConcept)}
          aria-hidden="true"
        />
      );
    }

    if (concept.type === 'text') {
      return (
        <PaymentConceptText
          key={concept.name}
          label={getConceptLabel(concept)}
          name={concept.name}
          value={concept.value}
        />
      );
    }

    return (
      <PaymentConceptAmount
        key={concept.name}
        value={concept.value}
        label={getConceptLabel(concept)}
        name={concept.name}
      />
    );
  };

  return (
    <div
      className={cx(styles.paymentPlanOption, {
        [styles.gridSelectedOption]: isGridVariant && selected,
      })}
      data-testid={`payment-plan-option-${plan.name}`}
    >
      <div
        className={cx(styles.paymentPlanContent, {
          [styles.selected]: selected,
        })}
      >
        <h4>{plan.displayName}</h4>

        <div className={styles.paymentBreakdown}>
          {breakdown.map(renderBreakdownItem)}
        </div>

        <div className={styles.ctaRow}>
          <Button
            type="button"
            size="sm"
            variant={selected ? 'secondary' : 'primary'}
            className={cx({
              [styles.gridChooseButton]: isGridVariant && !selected,
            })}
            isSubmitting={selected && loading}
            disabled={selected || loading}
            onClick={onSelect}
          >
            {selected ? (
              <>
                <Asset
                  icon={CheckmarkIcon}
                  description="selected payment plan"
                  width={16}
                  height={16}
                />
                {selectedLabel}
              </>
            ) : (
              'Choose'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
